import type { ThemeColorName } from '@examdojo/styles';
import type { GeometryElement } from 'jsxgraph';
import type { ElementTheme, PointStyle } from '../theme/theme.model';
import type { JsxgraphThemeService } from '../../jsxgraph-theme.service';
import type { ElementCreatorContext } from '../core/board.types';
import { deepMerge } from '../core/deepMerge';
import { bindLabel, createPointLabelContext } from '../label/label.bind';
import { resolveLabelConfig } from '../label/label.model';
import { buildSnappingAttributes } from './snapping';
import type { PointArgs, PointCoordsInput, PointResult } from './point.model';

/**
 * Resolve a theme color name to a CSS color string.
 */
function resolveColor(themeService: JsxgraphThemeService, color: ThemeColorName | undefined): string | undefined {
  if (!color) {
    return undefined;
  }
  return themeService.getColor(color);
}

/**
 * Normalize point coordinates to JSXGraph format.
 * Handles both static [x, y] and dynamic [() => x, () => y] inputs.
 */
function normalizeCoords(coords: PointCoordsInput): [number | (() => number), number | (() => number)] {
  return coords;
}

/**
 * Build JSXGraph point attributes from style configuration.
 * Maps our simplified color model to JSXGraph's fillColor/strokeColor.
 */
function buildPointAttributes(
  themeService: JsxgraphThemeService,
  baseStyle: Required<Omit<PointStyle, 'fixed' | 'strokeColor' | 'label' | 'snapping' | 'showGrabHandle'>>,
  styleOverrides?: PointStyle,
): Record<string, unknown> {
  const style = deepMerge(baseStyle, styleOverrides);

  // Our 'color' is the fill color (main visual color)
  const fillColor = resolveColor(themeService, style.color);
  // strokeColor is optional - if not set, use transparent (no stroke)
  const strokeColor = styleOverrides?.strokeColor
    ? resolveColor(themeService, styleOverrides.strokeColor)
    : 'transparent';

  return {
    size: style.size,
    face: style.face,
    strokeColor,
    fillColor,
    fillOpacity: style.fillOpacity,
    strokeWidth: styleOverrides?.strokeColor ? style.strokeWidth ?? 1 : 0,
    fixed: styleOverrides?.fixed ?? false,
    visible: style.visible,
    showInfobox: false,
    withLabel: false, // We handle labels separately
  };
}

/**
 * Create a point element with optional label.
 * Label and snapping are now configured via the style property.
 *
 * @param ctx - Element creator context (board, container)
 * @param themeService - Theme service for color resolution
 * @param theme - Element theme with defaults
 * @param args - Point creation arguments
 * @returns Point result with element and optional label
 */
export function createPoint(
  ctx: ElementCreatorContext,
  themeService: JsxgraphThemeService,
  theme: ElementTheme,
  args: PointArgs,
): PointResult {
  const { board } = ctx;
  const { coords, name = '', style } = args;

  // Build point attributes (visual)
  const visualAttrs = buildPointAttributes(themeService, theme.point, style);

  // Build snapping attributes (behavioral) - now from style.snapping
  const snappingAttrs = buildSnappingAttributes(ctx, style?.snapping);

  // Merge and create the point
  const normalizedCoords = normalizeCoords(coords);
  const point = board.create('point', normalizedCoords, {
    ...visualAttrs,
    ...snappingAttrs,
  }) as GeometryElement;

  const result: PointResult = { point };

  // Create label if configured - now from style.label
  const labelConfig = resolveLabelConfig(style?.label, {
    color: style?.color ?? 'neutral',
  });

  if (labelConfig && name) {
    result.label = bindLabel({
      ctx,
      themeService,
      element: point,
      config: labelConfig,
      contextProvider: createPointLabelContext(name, point),
    });
  }

  return result;
}
